export type SubscriptionCheckoutTier = 'essential' | 'growth' | 'portfolio'
export type SubscriptionCheckoutBilling = 'month' | 'year'

export interface SubscriptionCheckoutIntent {
  tier: SubscriptionCheckoutTier
  billing: SubscriptionCheckoutBilling
}

export interface SubscriptionTierDisplay {
  name: string
  description: string
  monthlyPrice: number | null
  annualPrice: number | null
  urlLimit: number
  domainLimit: number
  teamMemberLimit: number
  features: string[]
}

const VALID_TIERS: SubscriptionCheckoutTier[] = ['essential', 'growth', 'portfolio']
const VALID_BILLING: SubscriptionCheckoutBilling[] = ['month', 'year']

export const SUBSCRIPTION_TIER_DISPLAYS: Record<SubscriptionCheckoutTier, SubscriptionTierDisplay> = {
  essential: {
    name: 'Essential',
    description: 'For one business with one site. We watch 50 pages weekly and tell you when answers start going stale.',
    monthlyPrice: 49,
    annualPrice: 470,
    urlLimit: 50,
    domainLimit: 1,
    teamMemberLimit: 2,
    features: ['Weekly checks', '30-day history', 'HubSpot Sync', 'Alerts when something moves'],
  },
  growth: {
    name: 'Growth',
    description: 'For growing teams. More pages, longer history, full HubSpot Sync.',
    monthlyPrice: 129,
    annualPrice: 1250,
    urlLimit: 200,
    domainLimit: 3,
    teamMemberLimit: 5,
    features: ['Weekly checks', '90-day history', 'HubSpot Sync', 'SuperSchema Registry listing'],
  },
  portfolio: {
    name: 'Portfolio',
    description: 'For agencies and operators running multiple brands. Daily cadence across the whole portfolio.',
    monthlyPrice: 299,
    annualPrice: 2900,
    urlLimit: 500,
    domainLimit: 10,
    teamMemberLimit: 20,
    features: ['Daily checks', '1-year history', 'SuperSchema Registry listing', 'Priority support'],
  },
}

export function isSubscriptionCheckoutTier(value: string | null | undefined): value is SubscriptionCheckoutTier {
  return VALID_TIERS.includes(value as SubscriptionCheckoutTier)
}

export function isSubscriptionCheckoutBilling(value: string | null | undefined): value is SubscriptionCheckoutBilling {
  return VALID_BILLING.includes(value as SubscriptionCheckoutBilling)
}

export function getSubscriptionCheckoutIntent(searchParams: URLSearchParams): SubscriptionCheckoutIntent | null {
  if (searchParams.get('checkout') !== 'subscription') return null

  const tier = searchParams.get('tier')
  const billing = searchParams.get('billing')
  if (!isSubscriptionCheckoutTier(tier) || !isSubscriptionCheckoutBilling(billing)) return null

  return { tier, billing }
}

export function buildSubscriptionCheckoutPath(intent: SubscriptionCheckoutIntent) {
  const params = new URLSearchParams({
    tier: intent.tier,
    billing: intent.billing,
  })
  return `/checkout/subscription?${params.toString()}`
}

export function buildSubscriptionAuthPath(basePath: '/sign-in' | '/sign-up', intent: SubscriptionCheckoutIntent) {
  const params = new URLSearchParams({
    checkout: 'subscription',
    tier: intent.tier,
    billing: intent.billing,
  })
  return `${basePath}?${params.toString()}`
}

export function buildSubscriptionSignUpPath(intent: SubscriptionCheckoutIntent) {
  return buildSubscriptionAuthPath('/sign-up', intent)
}

export function buildSubscriptionSignInPath(intent: SubscriptionCheckoutIntent) {
  return buildSubscriptionAuthPath('/sign-in', intent)
}

export function getSubscriptionTierDisplay(tier: SubscriptionCheckoutTier): SubscriptionTierDisplay {
  return SUBSCRIPTION_TIER_DISPLAYS[tier]
}
